import {t} from "@lingui/core/macro";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {FacebookIcon, getFacebookAuthToken} from "@khan/auth-facebook";
import type {LoginType} from "@khan/auth-shared";
import type {ApolloGqlClient} from "@khan/data-gql-apollo";
import {doNotTranslate} from "@khan/i18n";

import RequireReauth from "../require-reauth.tsx";

import ResponsiveLinkedAccountRow from "./responsive-linked-account-row.tsx";

interface Props {
    gqlClient: ApolloGqlClient;
    facebookId: string | null | undefined;
    facebookPrimaryEmail: string | null | undefined;
    identifier: string | null | undefined;
    isClasslinkUser: boolean;
    setWarningMessage: (message: string | null) => void;
    googlePrimaryEmail: string | null | undefined;
    appleId: string | null | undefined;
    microsoftId: string | null | undefined;
    renderThirdPartyDisconnectButton: (provider: LoginType) => React.ReactNode;
    linkAccountWith: (loginType: LoginType, token: string) => void;
}

export const LinkFacebookAccount: React.FC<Props> = ({
    facebookId,
    facebookPrimaryEmail,
    identifier,
    isClasslinkUser,
    setWarningMessage,
    googlePrimaryEmail,
    appleId,
    microsoftId,
    linkAccountWith,
    renderThirdPartyDisconnectButton,
}) => {
    const onConnectFacebook = () => {
        getFacebookAuthToken().then((token) => {
            linkAccountWith("facebook", token);
        });
    };

    const connectFacebookButton = (
        <RequireReauth
            identifier={identifier}
            showClasslinkAuthProvider={isClasslinkUser}
            onSuccess={onConnectFacebook}
            onFailure={() => {}}
            hasFacebookButton={false}
            // We check the other providers if the user has an
            // account registered with them
            hasGoogleButton={!!googlePrimaryEmail}
            hasAppleButton={!!appleId}
            hasMicrosoftButton={!!microsoftId}
        >
            {(invokeReauth) => (
                <Button
                    onClick={() => {
                        setWarningMessage(null);
                        invokeReauth();
                    }}
                    size="small"
                >
                    {t`Connect with Facebook`}
                </Button>
            )}
        </RequireReauth>
    );

    return (
        <ResponsiveLinkedAccountRow
            icon={
                <FacebookIcon ariaHidden={true} disabled={false} size="large" />
            }
            label={doNotTranslate`Facebook`}
            content={
                facebookId ? facebookPrimaryEmail || t`Connected` : undefined
            }
            action={
                facebookId
                    ? renderThirdPartyDisconnectButton("facebook")
                    : connectFacebookButton
            }
        />
    );
};

export default LinkFacebookAccount;
